import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Plus, Trash2, CheckCircle, XCircle, AlertTriangle, FileText } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import ManufacturingService from '@/api/manufacturing.service';
import { useToast } from '@/hooks/use-toast';

const qualityCheckSchema = z.object({
  checkNumber: z.string().min(1, 'Check number is required'),
  productionJobId: z.number().min(1, 'Production job is required'),
  checkType: z.enum(['incoming', 'in_process', 'final', 'random'], {
    required_error: 'Check type is required',
  }),
  inspector: z.string().optional(),
  checkDate: z.date({
    required_error: 'Check date is required',
  }),
  sampleSize: z.number().min(1, 'Sample size must be at least 1'),
  notes: z.string().optional(),
});

type QualityCheckFormData = z.infer<typeof qualityCheckSchema>;

interface QualityCheckFormProps {
  initialData?: Partial<QualityCheckFormData & { 
    id?: number; 
    publicId?: string; 
    status?: string;
    passedCount?: number;
    failedCount?: number;
    defectTypes?: string[];
    corrective_action?: string;
  }>;
  onSubmit: (data: QualityCheckFormData & { 
    inspectionCriteria: InspectionCriterion[];
    defectTypes: string[];
  }) => void;
  onCancel: () => void;
  isLoading?: boolean;
  mode: 'create' | 'edit';
}

interface ProductionJob {
  id: number;
  publicId: string;
  jobNumber: string;
  productName: string;
  recipeName: string;
  status: string;
}

interface InspectionCriterion {
  id: string;
  name: string;
  description: string;
  type: 'visual' | 'measurement' | 'functional' | 'dimensional';
  specification: string;
  tolerance?: string;
  required: boolean;
}

interface DefectType {
  id: string;
  name: string;
  severity: 'minor' | 'major' | 'critical';
  description: string;
}

const QualityCheckForm: React.FC<QualityCheckFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
  mode
}) => {
  const { toast } = useToast();
  const [productionJobs, setProductionJobs] = useState<ProductionJob[]>([]);
  const [inspectionCriteria, setInspectionCriteria] = useState<InspectionCriterion[]>([]);
  const [defectTypes, setDefectTypes] = useState<DefectType[]>([]);
  const [selectedDefects, setSelectedDefects] = useState<string[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(false);

  const businessId = parseInt(localStorage.getItem('selectedBusiness') ? JSON.parse(localStorage.getItem('selectedBusiness')!).id : '1');

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset
  } = useForm<QualityCheckFormData>({
    resolver: zodResolver(qualityCheckSchema),
    defaultValues: {
      checkNumber: initialData?.checkNumber || '',
      productionJobId: initialData?.productionJobId || 0,
      checkType: initialData?.checkType || 'in_process',
      inspector: initialData?.inspector || '',
      checkDate: initialData?.checkDate ? new Date(initialData.checkDate) : new Date(),
      sampleSize: initialData?.sampleSize || 10,
      notes: initialData?.notes || '',
    }
  });

  const checkDate = watch('checkDate');
  const selectedJobId = watch('productionJobId');

  useEffect(() => {
    fetchProductionJobs();
    initializeInspectionCriteria();
    initializeDefectTypes();
  }, [businessId]);

  useEffect(() => {
    if (initialData && mode === 'edit') {
      reset({
        checkNumber: initialData.checkNumber || '',
        productionJobId: initialData.productionJobId || 0,
        checkType: initialData.checkType || 'in_process',
        inspector: initialData.inspector || '',
        checkDate: initialData.checkDate ? new Date(initialData.checkDate) : new Date(),
        sampleSize: initialData.sampleSize || 10,
        notes: initialData.notes || '',
      });
      setSelectedDefects(initialData.defectTypes || []);
    }
  }, [initialData, mode, reset]);

  const fetchProductionJobs = async () => {
    try {
      setLoadingJobs(true);
      const response = await ManufacturingService.getProductionJobs(businessId);
      if (response.data.result === 'success') {
        const activeJobs = response.data.productionJobs?.filter((job: ProductionJob) => 
          job.status === 'in_progress' || job.status === 'pending'
        ) || [];
        setProductionJobs(activeJobs);
      }
    } catch (error) {
      console.error('Error fetching production jobs:', error);
      toast({
        title: "Error",
        description: "Failed to fetch production jobs",
        variant: "destructive",
      });
    } finally {
      setLoadingJobs(false);
    }
  };

  const initializeInspectionCriteria = () => {
    // Mock inspection criteria - in real app, this would come from an API
    const mockCriteria: InspectionCriterion[] = [
      {
        id: '1',
        name: 'Visual Appearance',
        description: 'Check for surface defects, color consistency, and overall appearance',
        type: 'visual',
        specification: 'No visible defects, uniform color',
        required: true
      },
      {
        id: '2',
        name: 'Dimensional Accuracy',
        description: 'Verify dimensions meet specifications',
        type: 'dimensional',
        specification: '±0.1mm tolerance',
        tolerance: '0.1mm',
        required: true
      },
      {
        id: '3',
        name: 'Functional Test',
        description: 'Test product functionality and performance',
        type: 'functional',
        specification: 'All functions operate correctly',
        required: true
      },
      {
        id: '4',
        name: 'Weight Check',
        description: 'Verify product weight within specifications',
        type: 'measurement',
        specification: '100g ±5g',
        tolerance: '5g',
        required: false
      }
    ];
    setInspectionCriteria(mockCriteria);
  };

  const initializeDefectTypes = () => {
    // Mock defect types - in real app, this would come from an API
    const mockDefects: DefectType[] = [
      { id: '1', name: 'Surface Scratch', severity: 'minor', description: 'Minor surface scratches' },
      { id: '2', name: 'Color Variation', severity: 'minor', description: 'Slight color inconsistency' },
      { id: '3', name: 'Dimensional Error', severity: 'major', description: 'Dimensions outside tolerance' },
      { id: '4', name: 'Functional Failure', severity: 'critical', description: 'Product does not function' },
      { id: '5', name: 'Material Defect', severity: 'major', description: 'Material quality issues' },
      { id: '6', name: 'Assembly Error', severity: 'major', description: 'Incorrect assembly' }
    ];
    setDefectTypes(mockDefects);
  };

  const generateCheckNumber = () => {
    const timestamp = Date.now().toString().slice(-6);
    const randomNum = Math.floor(Math.random() * 100).toString().padStart(2, '0');
    return `QC-${timestamp}${randomNum}`;
  };

  const handleGenerateNumber = () => {
    setValue('checkNumber', generateCheckNumber());
  };

  const handleDefectToggle = (defectId: string) => {
    setSelectedDefects(prev => 
      prev.includes(defectId) 
        ? prev.filter(id => id !== defectId)
        : [...prev, defectId]
    );
  };

  const addInspectionCriterion = () => {
    const newCriterion: InspectionCriterion = {
      id: Date.now().toString(),
      name: '',
      description: '',
      type: 'visual',
      specification: '',
      required: false
    };
    setInspectionCriteria([...inspectionCriteria, newCriterion]);
  };

  const updateInspectionCriterion = (id: string, field: keyof InspectionCriterion, value: any) => {
    setInspectionCriteria(prev => 
      prev.map(criterion => 
        criterion.id === id ? { ...criterion, [field]: value } : criterion
      )
    );
  };

  const removeInspectionCriterion = (id: string) => {
    setInspectionCriteria(prev => prev.filter(criterion => criterion.id !== id));
  };

  const getSeverityBadge = (severity: string) => {
    const severityConfig = {
      minor: { variant: 'outline' as const, color: 'text-yellow-600' },
      major: { variant: 'secondary' as const, color: 'text-orange-600' },
      critical: { variant: 'destructive' as const, color: 'text-red-600' },
    };
    
    const config = severityConfig[severity as keyof typeof severityConfig] || severityConfig.minor;
    return <Badge variant={config.variant} className={config.color}>{severity}</Badge>;
  };

  const onFormSubmit = (data: QualityCheckFormData) => {
    // Validate that at least one inspection criterion is defined
    const validCriteria = inspectionCriteria.filter(c => c.name.trim() && c.specification.trim());
    if (validCriteria.length === 0) {
      toast({
        title: "Validation Error",
        description: "At least one inspection criterion is required",
        variant: "destructive",
      });
      return;
    }

    onSubmit({
      ...data,
      inspectionCriteria: validCriteria,
      defectTypes: selectedDefects
    });
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Basic Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="checkNumber">Check Number</Label>
              <div className="flex gap-2">
                <Input
                  id="checkNumber"
                  {...register('checkNumber')}
                  placeholder="Enter check number"
                  className={errors.checkNumber ? 'border-red-500' : ''}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleGenerateNumber}
                  className="whitespace-nowrap"
                >
                  Generate
                </Button>
              </div>
              {errors.checkNumber && (
                <p className="text-sm text-red-500">{errors.checkNumber.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="productionJobId">Production Job</Label>
              <Select
                value={watch('productionJobId')?.toString() || ''}
                onValueChange={(value) => setValue('productionJobId', parseInt(value))}
              >
                <SelectTrigger className={errors.productionJobId ? 'border-red-500' : ''}>
                  <SelectValue placeholder="Select production job" />
                </SelectTrigger>
                <SelectContent>
                  {loadingJobs ? (
                    <SelectItem value="loading" disabled>Loading jobs...</SelectItem>
                  ) : (
                    productionJobs.map((job) => (
                      <SelectItem key={job.id} value={job.id.toString()}>
                        {job.jobNumber} - {job.productName}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {errors.productionJobId && (
                <p className="text-sm text-red-500">{errors.productionJobId.message}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="checkType">Check Type</Label>
              <Select
                value={watch('checkType') || ''}
                onValueChange={(value) => setValue('checkType', value as any)}
              >
                <SelectTrigger className={errors.checkType ? 'border-red-500' : ''}>
                  <SelectValue placeholder="Select check type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="incoming">Incoming Inspection</SelectItem>
                  <SelectItem value="in_process">In-Process Inspection</SelectItem>
                  <SelectItem value="final">Final Inspection</SelectItem>
                  <SelectItem value="random">Random Inspection</SelectItem>
                </SelectContent>
              </Select>
              {errors.checkType && (
                <p className="text-sm text-red-500">{errors.checkType.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="inspector">Inspector</Label>
              <Input
                id="inspector"
                {...register('inspector')}
                placeholder="Inspector name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="sampleSize">Sample Size</Label>
              <Input
                id="sampleSize"
                type="number"
                min="1"
                {...register('sampleSize', { valueAsNumber: true })}
                className={errors.sampleSize ? 'border-red-500' : ''}
              />
              {errors.sampleSize && (
                <p className="text-sm text-red-500">{errors.sampleSize.message}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Check Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !checkDate && "text-muted-foreground",
                    errors.checkDate && "border-red-500"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {checkDate ? format(checkDate, "PPP") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={checkDate}
                  onSelect={(date) => setValue('checkDate', date || new Date())}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            {errors.checkDate && (
              <p className="text-sm text-red-500">{errors.checkDate.message}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Inspection Criteria */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5" />
              Inspection Criteria
            </CardTitle>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addInspectionCriterion}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Add Criterion
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {inspectionCriteria.map((criterion, index) => (
            <div key={criterion.id} className="p-4 border rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Criterion {index + 1}</h4>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeInspectionCriterion(criterion.id)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={criterion.name}
                    onChange={(e) => updateInspectionCriterion(criterion.id, 'name', e.target.value)}
                    placeholder="Criterion name"
                  />
                </div>
                <div>
                  <Label>Type</Label>
                  <Select
                    value={criterion.type}
                    onValueChange={(value) => updateInspectionCriterion(criterion.id, 'type', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="visual">Visual</SelectItem>
                      <SelectItem value="measurement">Measurement</SelectItem>
                      <SelectItem value="functional">Functional</SelectItem>
                      <SelectItem value="dimensional">Dimensional</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label>Description</Label>
                <Input
                  value={criterion.description}
                  onChange={(e) => updateInspectionCriterion(criterion.id, 'description', e.target.value)}
                  placeholder="Detailed description"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Specification</Label>
                  <Input
                    value={criterion.specification}
                    onChange={(e) => updateInspectionCriterion(criterion.id, 'specification', e.target.value)}
                    placeholder="Acceptance criteria"
                  />
                </div>
                <div>
                  <Label>Tolerance (optional)</Label>
                  <Input
                    value={criterion.tolerance || ''}
                    onChange={(e) => updateInspectionCriterion(criterion.id, 'tolerance', e.target.value)}
                    placeholder="±0.1mm"
                  />
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id={`required-${criterion.id}`}
                  checked={criterion.required}
                  onCheckedChange={(checked) => updateInspectionCriterion(criterion.id, 'required', checked)}
                />
                <Label htmlFor={`required-${criterion.id}`}>Required criterion</Label>
              </div>
            </div>
          ))}
          
          {inspectionCriteria.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <CheckCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No inspection criteria defined</p>
              <p className="text-sm">Add criteria to define what should be checked</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Potential Defect Types */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <XCircle className="h-5 w-5" />
            Potential Defect Types
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {defectTypes.map((defect) => (
              <div
                key={defect.id}
                className={cn(
                  "flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors",
                  selectedDefects.includes(defect.id) 
                    ? "border-blue-500 bg-blue-50" 
                    : "border-gray-200 hover:border-gray-300"
                )}
                onClick={() => handleDefectToggle(defect.id)}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-medium text-sm">{defect.name}</p>
                    {getSeverityBadge(defect.severity)}
                  </div>
                  <p className="text-xs text-muted-foreground">{defect.description}</p>
                </div>
                {selectedDefects.includes(defect.id) && (
                  <div className="w-2 h-2 bg-blue-500 rounded-full ml-2"></div>
                )}
              </div>
            ))}
          </div>
          {selectedDefects.length > 0 && (
            <div className="mt-4">
              <p className="text-sm text-muted-foreground">
                Selected defect types: {selectedDefects.length}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Notes */}
      <Card>
        <CardHeader>
          <CardTitle>Additional Notes</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            {...register('notes')}
            placeholder="Any additional notes or special instructions for this quality check"
            rows={4}
          />
        </CardContent>
      </Card>

      {/* Form Actions */}
      <div className="flex justify-end gap-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? 'Saving...' : mode === 'create' ? 'Create Quality Check' : 'Update Quality Check'}
        </Button>
      </div>
    </form>
  );
};

export default QualityCheckForm; 